/**
 * Product URL Utilities
 *
 * Functions for building consistent product URLs across the application.
 * Used by PLPs, wishlist, sliders, and other product listings.
 */

import { materialValueToSlug } from '@/lib/listingData/plpConfig';

// =============================================================================
// Types
// =============================================================================

interface ProductUrlData {
  /** Product SKU (objectID) */
  sku: string;
  /** Product type: watch, strap, eyewear */
  productType: 'watch' | 'strap' | 'eyewear' | 'accessory';
  /** Collections array with slug */
  collections?: Array<{ name?: string; slug?: string }>;
  /** Model with slug */
  model?: { name?: Record<string, string>; slug?: string };
  /** Strap material (for strap URLs) */
  material?: string;
}

// =============================================================================
// URL Builders
// =============================================================================

/** Normalize SKU for URL paths — spaces to hyphens, uppercase per spec §12. */
function normalizeSkuForUrl(sku: string): string {
  return sku.replace(/\s+/g, '-').toUpperCase();
}

/**
 * Build a watch PDP URL.
 *
 * Returns a locale-relative pathname (no `/<locale>/` prefix). The consuming
 * `<Link>` is `lib/i18n/navigation`, which is configured with
 * `localePrefix: 'always'` and adds the locale itself; pre-prefixing here
 * would cause it to be doubled on click.
 *
 * Format: /watches/{collection-slug}/{model-slug}/{sku}
 * Example: /watches/avenger/avenger-automatic-gmt-44-my2023/A32320101C1A1
 */
export function buildWatchUrl(collectionSlug: string, modelSlug: string, sku: string): string {
  const cleanCollectionSlug = collectionSlug?.toLowerCase() || 'collection';
  const cleanModelSlug = modelSlug?.toLowerCase() || 'model';

  return `/watches/${cleanCollectionSlug}/${cleanModelSlug}/${normalizeSkuForUrl(sku)}`;
}

/**
 * Build a strap PDP URL. Locale-relative; see {@link buildWatchUrl}.
 *
 * Format: /straps/{material-slug}/{sku}
 * Example: /straps/leather/123456
 */
export function buildStrapUrl(materialSlug: string, sku: string): string {
  const cleanMaterialSlug = materialSlug?.toLowerCase() || 'material';

  return `/straps/${cleanMaterialSlug}/${normalizeSkuForUrl(sku)}`;
}

/**
 * Breitling parity: strap PDP `[material]` must use the canonical slug derived from
 * Saleor/Algolia material (e.g. `leather`), not a localized label slug (`kalbsleder`).
 * Returns a locale-prefixed redirect target, or null when the URL is already canonical.
 */
export function getStrapPdpCanonicalRedirectPath(
  lang: string,
  urlMaterial: string,
  sku: string,
  saleorMaterialName: string | null | undefined
): string | null {
  const canonicalSlug = materialValueToSlug(saleorMaterialName || urlMaterial);
  if (canonicalSlug === urlMaterial.toLowerCase()) {
    return null;
  }

  return `/${lang}${buildStrapUrl(canonicalSlug, sku)}/`;
}

/**
 * Build an eyewear PDP URL. Locale-relative; see {@link buildWatchUrl}.
 *
 * Format: /eyewear/{model-slug}/{sku}
 * Example: /eyewear/sport-sunglasses/EW001
 */
export function buildEyewearUrl(modelSlug: string, sku: string): string {
  const cleanModelSlug = modelSlug?.toLowerCase() || 'model';

  return `/eyewear/${cleanModelSlug}/${normalizeSkuForUrl(sku)}`;
}

/**
 * Build a product URL based on product type.
 *
 * Returns a locale-relative pathname; see {@link buildWatchUrl}.
 */
export function buildProductUrl(product: ProductUrlData): string {
  const { sku, productType, collections, model, material } = product;

  switch (productType) {
    case 'watch':
    case 'eyewear': {
      // For watches and eyewear, use collection + model structure
      const collectionSlug = collections?.[0]?.slug || 'collection';
      const modelSlug = model?.slug || 'model';

      if (productType === 'eyewear') {
        return buildEyewearUrl(modelSlug, sku);
      }
      return buildWatchUrl(collectionSlug, modelSlug, sku);
    }

    case 'strap': {
      // For straps, use material
      const materialSlug = material || 'material';
      return buildStrapUrl(materialSlug, sku);
    }

    case 'accessory':
    default: {
      // Fallback for accessories or unknown types
      return `/accessories/${sku}`;
    }
  }
}
